import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axiosInstance from "../axiosConfig";

const normalizeDepartment = value => String(value || "")
  .trim()
  .replace(/\s+/g, " ")
  .toUpperCase()
  .replace(/^OPERATION\s*:\s*/, "")
  .replace(/^[\s\-–—:]+|[\s\-–—:]+$/g, "")
  .replace(/\bCUTING\b/g, "CUTTING")
  .replace(/\s+(?:DEPT|DEPARTMENT)$/, "")
  .replace(/^.*\b(?:PRESSING|GLOWING|GLUING)\b.*$/, "PRESSING")
  .trim();

const displayDepartment = value => String(value || "")
  .trim()
  .replace(/\s+/g, " ")
  .toUpperCase()
  .replace(/\bCUTING\b/g, "CUTTING")
  .replace(/\s+(?:DEPT|DEPARTMENT)$/, "")
  .trim();

const displayValue = value => String(value ?? "").trim() || "-";

const uniqueLotsById = lots => [...lots.reduce((unique, lot) => {
  const key = String(lot?.lotId || "").trim().toUpperCase();
  if (!key) return unique;
  const current = unique.get(key);
  const exactSheetName = candidate => String(candidate?.sheetName || "").trim().toUpperCase() === key;
  if (!current || (!exactSheetName(current) && exactSheetName(lot))) unique.set(key, lot);
  return unique;
}, new Map()).values()];

export default function FoamCuttingSharePointLots({
  department,
  displayDepartment: displayDepartmentValue = "",
  showCuttingInstructions = false,
}) {
  const workerDepartment = normalizeDepartment(department);
  const isCuttingDepartment = Boolean(workerDepartment);
  const visibleDepartment = displayDepartment(displayDepartmentValue) || workerDepartment;
  const viewingDepartmentLabel = visibleDepartment === "PRESSING" ? "PRESSING / GLOWING" : visibleDepartment;
  const departmentLabel = visibleDepartment
    .toLowerCase()
    .replace(/\b\w/g, character => character.toUpperCase());
  const [lots, setLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState(null);
  const [lotDetails, setLotDetails] = useState(null);
  const [loadingLots, setLoadingLots] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const loadLotDetails = useCallback(async option => {
    setSelectedLot(option);
    setLotDetails(null);
    setError("");
    if (!option) return;

    try {
      setLoadingDetails(true);
      const response = await axiosInstance.get("/api/foam-cutting-sharepoint/lot", {
        params: { worksheetId: option.value, department: workerDepartment },
      });
      setLotDetails(response.data?.lot || null);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Selected Lot ID ka Excel data fetch nahi ho paya.");
    } finally {
      setLoadingDetails(false);
    }
  }, [workerDepartment]);

  useEffect(() => {
    if (!isCuttingDepartment) {
      setLots([]);
      setSelectedLot(null);
      setLotDetails(null);
      setError("");
      return;
    }

    let active = true;
    const loadLots = async () => {
      setLoadingLots(true);
      setError("");
      try {
        const response = await axiosInstance.get("/api/foam-cutting-sharepoint/lots", {
          params: { department: workerDepartment },
        });
        if (active) {
          const visibleLots = uniqueLotsById((response.data?.lots || [])
            .filter(lot => normalizeDepartment(lot.department) === workerDepartment));
          setLots(visibleLots);

          if (visibleLots.length) {
            const firstLot = visibleLots[0];
            await loadLotDetails({ value: firstLot.worksheetId, label: firstLot.lotId, lot: firstLot });
          } else {
            setSelectedLot(null);
            setLotDetails(null);
          }
        }
      } catch (requestError) {
        if (active) {
          const errorCode = requestError.response?.data?.code;
          setError(errorCode === "GRAPH_TOKEN_NOT_CONFIGURED"
            ? "Microsoft Excel connection configure nahi hai. IT administrator se contact karein."
            : (requestError.response?.data?.message || "SharePoint se Lot IDs fetch nahi ho paye."));
        }
      } finally {
        if (active) setLoadingLots(false);
      }
    };

    loadLots();
    return () => { active = false; };
  }, [isCuttingDepartment, loadLotDetails, reloadKey, workerDepartment]);

  const options = useMemo(() => lots.map(lot => ({
    value: lot.worksheetId,
    label: lot.lotId,
    lot,
  })), [lots]);

  if (!isCuttingDepartment) return null;

  return (
    <section className="card border-0 shadow mb-4 rounded-4" aria-labelledby="foamCuttingExcelTitle">
      <div className="card-header border-0 text-white p-3 p-sm-4 rounded-top-4" style={{ background: "linear-gradient(135deg, #064e3b 0%, #0f766e 55%, #0d9488 100%)" }}>
        <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2">
          <div>
            <div className="small text-uppercase opacity-75 fw-semibold mb-1">Live SharePoint Excel</div>
            <h2 className="mb-0 fw-bold" id="foamCuttingExcelTitle" style={{ fontSize: "clamp(1.25rem, 4.5vw, 1.75rem)" }}>{departmentLabel} Lot Details</h2>
          </div>
          <span className="badge rounded-pill bg-light text-success align-self-start align-self-sm-center px-3 py-2">
            <i className="bi bi-file-earmark-excel-fill me-2" />{lots.length} Lots
          </span>
        </div>
      </div>

      <div className="card-body p-3 p-sm-4">
        {loadingLots ? (
          <div className="border rounded-3 bg-light py-5 px-3 text-center" role="status" aria-live="polite">
            <div className="spinner-border text-success" style={{ width: "3rem", height: "3rem" }} aria-hidden="true" />
            <div className="fw-bold fs-5 mt-3">Lot IDs load ho rahe hain...</div>
            <div className="text-muted small mt-1">Live SharePoint workbook ke worksheets check kiye ja rahe hain.</div>
          </div>
        ) : (
          <>
            <label className="form-label fw-bold" htmlFor="foamCuttingLotSelect">Lot ID select karein</label>
            <Select
              inputId="foamCuttingLotSelect"
              value={selectedLot}
              options={options}
              onChange={loadLotDetails}
              isClearable={false}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              maxMenuHeight={260}
              placeholder="Lot ID type karke search karein..."
              noOptionsMessage={({ inputValue }) => inputValue ? "Matching Lot ID nahi mila" : "Koi Lot ID available nahi hai"}
              styles={{
                control: base => ({ ...base, minHeight: 48, borderRadius: 8 }),
                menuPortal: base => ({ ...base, zIndex: 3000 }),
                menu: base => ({ ...base, zIndex: 3000 }),
                option: base => ({ ...base, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),
              }}
            />
          </>
        )}

        {!loadingLots && lots.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mt-2">
            <span className="badge border bg-success-subtle text-success border-success-subtle">
              {departmentLabel}: {lots.length}
            </span>
          </div>
        )}

        {error && (
          <div className="alert alert-danger mt-3 mb-0 d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3" role="alert">
            <div><i className="bi bi-exclamation-triangle-fill me-2" />{error}</div>
            <button type="button" className="btn btn-outline-danger btn-sm rounded-pill px-3 align-self-start align-self-sm-center" onClick={() => setReloadKey(value => value + 1)} disabled={loadingLots}>
              <i className="bi bi-arrow-clockwise me-2" />Retry
            </button>
          </div>
        )}

        {loadingDetails && (
          <div className="text-center py-5">
            <div className="spinner-border text-success" role="status"><span className="visually-hidden">Loading</span></div>
            <div className="text-muted mt-2">Selected Excel range fetch ho rahi hai...</div>
          </div>
        )}

        {!loadingDetails && selectedLot && !lotDetails && !error && (
          <div className="text-center text-muted py-5">Lot details available nahi hain.</div>
        )}

        {!loadingDetails && lotDetails && (
          <div className="mt-4">
            <div className="rounded-3 border bg-light p-3 mb-3">
              <div className="row g-3">
                <div className="col-sm-6 col-lg-4"><small className="text-muted d-block">Lot ID</small><strong>{displayValue(lotDetails.lotId)}</strong></div>
                <div className="col-sm-6 col-lg-4"><small className="text-muted d-block">Viewing Department</small><strong>{viewingDepartmentLabel}</strong></div>
                <div className="col-sm-6 col-lg-4"><small className="text-muted d-block">Sheet Department</small><strong>{displayValue(lotDetails.department)}</strong></div>
                <div className="col-12"><small className="text-muted d-block">Name</small><strong>{displayValue(lotDetails.name)}</strong></div>
                {showCuttingInstructions && workerDepartment === "BELT CUTTING" && lotDetails.description && <div className="col-12">
                  <div className="bg-white border rounded-3 p-3">
                    <small className="text-muted d-block mb-1">Cutting Instructions</small>
                    <strong style={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{lotDetails.description}</strong>
                  </div>
                </div>}
                {[
                  { label: "Total Lot", qty: lotDetails.totalLotQty, weight: lotDetails.totalLotWeight },
                  { label: "Black Belt", qty: lotDetails.blackBeltQty, weight: lotDetails.blackBeltWeight },
                  { label: "Brown Belt", qty: lotDetails.brownBeltQty, weight: lotDetails.brownBeltWeight },
                  { label: "Tan Belt", qty: lotDetails.tanBeltQty, weight: lotDetails.tanBeltWeight },
                ].map(item => <div className="col-6 col-lg-3" key={item.label}>
                  <div className="bg-white border rounded-3 p-3 h-100">
                    <small className="text-muted d-block mb-1">{item.label}</small>
                    <div><span className="text-muted small">Qty: </span><strong>{displayValue(item.qty)}</strong></div>
                    <div><span className="text-muted small">Weight: </span><strong>{displayValue(item.weight)}</strong></div>
                  </div>
                </div>)}
                <div className="col-sm-6"><small className="text-muted d-block">Start Date</small><strong>{displayValue(lotDetails.startDate)}</strong></div>
                <div className="col-sm-6"><small className="text-muted d-block">End Date</small><strong>{displayValue(lotDetails.endDate)}</strong></div>
              </div>
            </div>

            {(lotDetails.sections || []).map(section => (
              <div className="border rounded-3 overflow-hidden mb-3" key={`${section.headerRow}-${section.processName}`}>
                <div className="px-3 py-2 fw-bold text-white" style={{ backgroundColor: "#334155" }}>{section.processName}</div>
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="px-3">Rexine Name</th>
                        <th>Colour</th>
                        <th className="text-end">Planned</th>
                        <th className="text-end">Reject</th>
                        <th className="text-end">Less</th>
                        <th className="text-end">Final</th>
                        <th className="px-3">Worker</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.items.flatMap(item => item.colors.map((entry, colorIndex) => (
                        <tr key={`${item.row}-${entry.color}`}>
                          <td className="px-3" style={{ minWidth: 220 }}>{colorIndex === 0 ? displayValue(item.material) : ""}</td>
                          <td><span className={`badge ${entry.color === "BLACK" ? "bg-dark" : entry.color === "BROWN" ? "bg-warning text-dark" : "bg-secondary"}`}>{entry.color}</span></td>
                          <td className="text-end">{displayValue(entry.plannedQty)}</td>
                          <td className="text-end">{displayValue(entry.rejectQty)}</td>
                          <td className="text-end">{displayValue(entry.lessQty)}</td>
                          <td className="text-end fw-semibold">{displayValue(entry.finalQty)}</td>
                          <td className="px-3" style={{ minWidth: 140 }}>{displayValue(entry.workerName)}</td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
